<div class="quick-access-panel flex h-full min-h-0 flex-col overflow-hidden">

  <!-- Panel header -->
  <div class="qa-panel-header flex items-center justify-between border-b border-surface-200 px-2 py-1 flex-shrink-0">
    <span class="text-sm font-semibold uppercase tracking-widest text-slate-500 select-none">
      Quick Access
    </span>
    <div class="flex items-center gap-1.5 ml-auto">
      <button
        type="button"
        class="qa-header-toggle-btn"
        (click)="toggleAllCardsExpanded($event)"
        [attr.aria-label]="areAllCardsExpanded ? 'Collapse all cards' : 'Expand all cards'"
        [attr.aria-pressed]="areAllCardsExpanded"
        [title]="areAllCardsExpanded ? 'Collapse All' : 'Expand All'">
        <span
          class="material-symbols-outlined qa-header-toggle-icon"
          [class.is-collapsed]="!areAllCardsExpanded"
          aria-hidden="true">
          expand_all
        </span>
      </button>
      <button
        type="button"
        class="qa-collapse-btn"
        (click)="collapse()"
        title="Collapse Quick Access panel">
        <span class="material-icons text-base" aria-hidden="true">chevron_left</span>
      </button>
    </div>
  </div>

  <!-- Cards scrollable area -->
  <div class="qa-scroll-area flex-1 overflow-y-auto p-1 space-y-2">

    <ng-container *ngFor="let card of cards; trackBy: trackByCardId">
      <div
        *ngIf="card.kind === 'notes' ? getNotesForCard(card.id).length > 0 : (getRelatedTags().length > 0 || getSelectedRelatedTags().length > 0)"
        class="break-inside-avoid border-2 border-primary rounded-md bg-surface-0 dark:bg-surface-900">

        <!-- Card header -->
        <div
          class="h-7 font-bold border-b border-[var(--borderColor)] flex items-center
                 justify-between relative px-1 cursor-pointer select-none"
          [ngClass]="{ 'qa-card-header--themed': card.kind === 'tags' }"
          [style.background-color]="card.color"
          (click)="toggleCardExpanded(card)">

          <button
            *ngIf="isSelectMode && card.kind === 'notes'"
            type="button"
            class="qa-card-select-all-btn"
            [attr.aria-label]="areAllCardNotesSelected(card.id) ? 'Unselect all notes in card' : 'Select all notes in card'"
            [attr.aria-pressed]="areAllCardNotesSelected(card.id)"
            [title]="areAllCardNotesSelected(card.id) ? 'Unselect All' : 'Select All'"
            (click)="toggleCardSelection(card.id, $event)">
            <span class="material-icons text-sm" aria-hidden="true">
              {{ areAllCardNotesSelected(card.id) ? 'check_box' : 'check_box_outline_blank' }}
            </span>
          </button>

          <div class="qa-card-title flex-1 text-center text-sm font-semibold text-black whitespace-nowrap
                      overflow-hidden text-ellipsis px-6">
            {{ card.title }}
            <span class="qa-card-count text-xs opacity-60 ml-1">({{ card.kind === 'notes' ? getNotesForCard(card.id).length : getTagsCardCount() }})</span>
          </div>

          <!-- Expand / Collapse toggle -->
          <button
            type="button"
            class="category-expand-toggle"
            [attr.aria-label]="card.isExpanded ? 'Collapse card' : 'Expand card'"
            [attr.aria-expanded]="card.isExpanded"
            (click)="toggleCardExpanded(card, $event)">
            <span
              class="material-symbols-outlined text-base transition-transform duration-200"
              [style.transform]="card.isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'">
              expand_more
            </span>
          </button>
        </div>

        <!-- Card body -->
        <div
          *ngIf="card.isExpanded"
          class="overflow-visible"
          [@slideDown]>
          <table *ngIf="card.kind === 'notes'" class="w-full">
            <tbody>
              <tr
                *ngFor="let note of getNotesForCard(card.id); trackBy: trackByNoteId"
                class="h-7 hover:bg-primary-400 cursor-pointer bg-primary-100 text-black dark:bg-surface-800 dark:text-surface-100"
                [ngClass]="{
                  'bg-[var(--project4Color)]': note.ProjectName === 'Work',
                  'bg-[var(--project1Color)]': note.ProjectName === 'NotesApp',
                  'bg-[var(--project2Color)]': note.ProjectName === 'Vivi/Olivia',
                  'bg-[var(--project3Color)]': note.ProjectName === 'Shopping',
                  'font-bold': isEmphasizedStatus(note.Status),
                  'italic text-[var(--textColorFaded)]':
                    isMutedStatus(note.Status),
                  'ring-2 ring-primary-600': isNoteSelected(note._id)
                }"
                (click)="clickRow(note)">

                <!-- Edit link / selection column -->
                <td class="px-1 border border-[var(--borderColor)] w-6 max-w-6 text-center">
                  <input
                    *ngIf="isSelectMode"
                    type="checkbox"
                    class="h-3.5 w-3.5 cursor-pointer"
                    [checked]="isNoteSelected(note._id)"
                    (click)="$event.stopPropagation()"
                    (change)="toggleSelection(note, $event)"
                    aria-label="Select note"
                  />
                  <a
                    *ngIf="!isSelectMode"
                    [routerLink]="['/edit', note._id]"
                    (click)="$event.stopPropagation()"
                    title="Edit note">
                    <i class="pi pi-pen-to-square text-sm text-slate-600"></i>
                  </a>
                </td>

                <!-- Title column -->
                <td class="px-1 border border-[var(--borderColor)] text-sm whitespace-normal cursor-pointer">
                  {{ getNoteYearPrefix(note) }}{{ note.Title | noteDisplayTitle: note.DescriptionHtml }}{{ getAttachmentFlag(note) }}
                </td>
              </tr>
            </tbody>
          </table>

          <div *ngIf="card.kind === 'tags'" class="qa-related-tags-list">
            <table class="w-full qa-related-tags-table">
              <tbody>
                <tr
                  *ngFor="let selectedTag of getSelectedRelatedTags(); trackBy: trackByRelatedTagId"
                  class="h-7 qa-related-tag-row qa-related-tag-row--selected">
                  <td class="px-1 border border-[var(--borderColor)] text-sm qa-related-tag-name-cell">
                    {{ selectedTag.label }}
                  </td>
                  <td class="px-1 border border-[var(--borderColor)] w-10 text-center">
                    <button
                      type="button"
                      class="qa-related-tag-action qa-related-tag-action--remove"
                      title="Unselect tag from Included Tags"
                      aria-label="Unselect tag from Included Tags"
                      (click)="removeRelatedTag(selectedTag.tagId, $event)">-</button>
                  </td>
                </tr>

                <tr
                  *ngFor="let relatedTag of getRelatedTags(); trackBy: trackByRelatedTagId"
                  class="h-7 qa-related-tag-row qa-related-tag-row--clickable"
                  role="button"
                  tabindex="0"
                  title="Include tag in filter (AND)"
                  (click)="addRelatedTag(relatedTag.tagId, $event)"
                  (keydown.enter)="addRelatedTag(relatedTag.tagId, $event)"
                  (keydown.space)="$event.preventDefault(); addRelatedTag(relatedTag.tagId, $event)">
                  <td class="px-1 border border-[var(--borderColor)] text-sm qa-related-tag-name-cell">
                    <div class="qa-related-tag-name-wrap">
                      <span class="qa-related-tag-name">{{ relatedTag.label }}</span>
                      <span class="qa-related-tag-count-bubble">{{ relatedTag.count }}</span>
                    </div>
                  </td>
                  <td class="px-1 border border-[var(--borderColor)] text-center w-24">
                    <div class="qa-related-tag-actions">
                      <button
                        type="button"
                        class="qa-related-tag-action qa-related-tag-action--include"
                        title="Include tag in filter (AND)"
                        aria-label="Include tag in filter (AND)"
                        (click)="addRelatedTag(relatedTag.tagId, $event)">+</button>
                      <button
                        type="button"
                        class="qa-related-tag-action qa-related-tag-action--exclude"
                        title="Exclude tag from filter"
                        aria-label="Exclude tag from filter"
                        (click)="excludeRelatedTag(relatedTag.tagId, $event)">⊘</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </ng-container>

  </div>
</div>
